
import { Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useMemo } from "react";
import { convertMetricValue, getMetricUnits, getMetricValue } from "./RTFOMetricsPanel";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOMetricsDataTable = ({ currentRTFO, darkMode }) => {

  const units = currentRTFO?.settings?.units || 'metric';

  // STYLES
  const classes = useStyles();

  const rows = useMemo(() => {
    if (!currentRTFO?.stages?.length > 0 || !currentRTFO?.stageData || Object.keys(currentRTFO.stageData)?.length === 0) return [];
    
    return [...currentRTFO.stages]?.reverse()?.map(stageName => {
      const stage = currentRTFO?.stageData?.[stageName];
      const metrics = stage?.metrics;
      const perfClosure = stage?.events?.perfClosure || [];
      const plugSlip = (stage?.events?.plugSlip || stage?.plugSlips) || [];
      const sandoff = stage?.events?.sandoff || [];
      const isolationIssue = stage?.events?.isolationIssue || [];
      const equipmentIssue = stage?.events?.equipmentIssue || [];
      const lostSuction = stage?.events?.lostSuction || [];
      const other = stage?.events?.other || [];
      const fracfeed = stage?.events?.fracfeed || [];
      const ballSeating = stage?.events?.ballSeating || [];
      const stageNumber = stageName?.split('_')?.[2];
      const wellName = stageName?.split('_')?.[1]?.toLowerCase();
      const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[String(stageNumber)?.replace(/[^0-9]+/g, '')]?.clusters || null;
      const depths = clusters ? Object.keys(clusters)?.map(d => clusters[d]?.shotDepth) : [];
      const topDepth = depths?.length > 0 ? Math.min(...depths) : 0;
      const bottomDepth = depths?.length > 0 ? Math.max(...depths) : 0;
      const averageDepth = depths?.length > 0 ? (depths?.reduce((a, b) => a + b, 0) / depths?.length) : 0; 

      return {
        id: stageName,
        stageName,
        wellName: stage?.wellName,
        stageNumber: String(stage?.stageNumber), 
        stageTime: Number(metrics?.stageTime?.toFixed(1)) || 0,
        numClusters: Number(metrics?.numClusters?.toFixed(1)) || 0,
        stageLength: +convertMetricValue('stageLength', metrics?.stageLength, units) || 0,
        tonsPerMin: +convertMetricValue('tonsPerMin', metrics?.tonsPerMin, units) || 0,
        fluidIntensity: +convertMetricValue('fluidIntensity', metrics?.fluidIntensity, units) || 0,
        sandPerFluid: +convertMetricValue('sandPerFluid', metrics?.sandPerFluid, units) || 0,
        fluidPerSand: +convertMetricValue('fluidPerSand', metrics?.fluidPerSand, units) || 0,
        tpm: +convertMetricValue('tpm', currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber?.replace(/[^0-9]+/g, '')]?.tpm, units) || 0,
        tpmActual: +convertMetricValue('tpm', metrics?.tpmCalc, units) || 0,
        totalProppant: +convertMetricValue('totalProppant', metrics?.tpmCalc * metrics?.stageLength, units) || 0,
        maxProppant: +convertMetricValue('proppant', metrics?.maxProppant, units) || 0,
        frPerFluid: +convertMetricValue('frPerFluid', metrics?.frPerFluid, units) || 0,
        frPerSand: +convertMetricValue('frPerSand', metrics?.frPerSand, units) || 0,
        frPerFluid2: +convertMetricValue('frPerFluid2', metrics?.frPerFluid2, units) || 0,
        frPerSand2: +convertMetricValue('frPerSand2', metrics?.frPerSand2, units) || 0,
        totalFr: +convertMetricValue('totalFr', getMetricValue('totalFr', stage, currentRTFO?.perfSchedule, currentRTFO?.settings), units),
        totalFr2: +convertMetricValue('totalFr2', getMetricValue('totalFr2', stage, currentRTFO?.perfSchedule, currentRTFO?.settings), units),
        breakdownPressure: +convertMetricValue('pressure', metrics?.breakdownPressure, units) || 0,
        preSandPressure: +convertMetricValue('pressure', metrics?.preSandPressure, units) || 0,
        preSandRate: +convertMetricValue('rate', metrics?.preSandRate, units) || 0,
        preSandPNwb: +convertMetricValue('pressure', metrics?.preSandPNwb, units) || 0,
        preSandPPerf: +convertMetricValue('pressure', metrics?.preSandPPerf, units) || 0,
        timeToPreSand: Number(metrics?.timeToPreSand?.toFixed(1)) || 0,
        cleanVolumeToPreSand: +convertMetricValue('volume', metrics?.cleanVolumeToPreSand, units) || 0,
        totalSlurryVolume: +convertMetricValue('volume', metrics?.totalSlurryVolume, units) || 0,
        totalCleanVolume: +convertMetricValue('volume', metrics?.totalCleanVolume, units) || 0,
        totalProducedVolume: +convertMetricValue('volume', metrics?.totalProducedVolume, units) || 0,
        overflushVolume: +convertMetricValue('volume', metrics?.overflushVolume, units) || 0,
        averagePumpingPressure: +convertMetricValue('pressure', metrics?.averagePumpingPressure, units) || 0,
        averagePumpingRate: +convertMetricValue('rate', metrics?.averagePumpingRate, units) || 0,
        wellSwapTime: Number(metrics?.wellSwapTime?.toFixed(2)) || 0,
        numShots: Number(metrics?.numShots?.toFixed(1)) || 0,
        pPerf: +convertMetricValue('pressure', metrics?.pPerf, units) || 0,
        n: Number(metrics?.N?.toFixed(1)) || 0,
        perfEfficiency: Number(metrics?.perfEfficiency?.toFixed(3)) || 0,
        isip: +convertMetricValue('pressure', metrics?.isip, units) || 0,
        isipBH: +convertMetricValue('pressure', metrics?.isipBH, units) || 0,
        fracGradient: +convertMetricValue('fracGradient', metrics?.fracGradient, units) || 0,
        perfClosure: perfClosure?.length || 0,
        plugSlip: plugSlip?.length || 0,
        sandoff: sandoff?.length || 0,
        isolationIssue: (isolationIssue?.length || 0) + (stage?.pumpdownParams?.postPerfISIP - stage?.pumpdownParams?.pumpdownISIP < 1 ? 1 : 0),
        equipmentIssue: equipmentIssue?.length || 0,
        lostSuction: lostSuction?.length || 0,
        other: other?.length || 0,
        fracfeed: fracfeed?.length || 0,
        ballSeating: ballSeating?.length || 0,
        aSqrtK: +convertMetricValue('fracArea', stage?.fracModel?.aSqrtK, units) || 0,
        fracArea: +convertMetricValue('fracArea', stage?.fracModel?.fracArea, units) || 0,
        halfLength: +convertMetricValue('halfLength', (stage?.fracModel?.halfLength || (stage?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight * 2))), units) || 0,
        fdiXf: +convertMetricValue('xf', getMetricValue('xf', stage, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO?.monitorEvents, currentRTFO?.fiberData), units) || 0,
        fdiVFR: +convertMetricValue('volume', getMetricValue('vfr', stage, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO?.monitorEvents, currentRTFO?.fiberData), units) || 0,
        percentEnergyLost: Number((stage?.fracEnergy?.percentEnergyLost * 100)?.toFixed(2)) || 0,
        topDepth: +convertMetricValue('depth', topDepth, units) || 0,
        bottomDepth: +convertMetricValue('depth', bottomDepth, units) || 0,
        averageDepth: +convertMetricValue('depth', averageDepth, units) || 0,
        pumpdownISIP: +convertMetricValue('isip', stage?.pumpdownParams?.pumpdownISIP, units) || 0,
        postPerfISIP: +convertMetricValue('isip', stage?.pumpdownParams?.postPerfISIP, units) || 0,
        deltaISIP: +convertMetricValue('isip', (stage?.pumpdownParams?.postPerfISIP - stage?.pumpdownParams?.pumpdownISIP), units) || 0,
      };
    });
  }, [currentRTFO?.fiberData, currentRTFO?.monitorEvents, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO.stageData, currentRTFO.stages, units]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage No.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageTime',
        headerName: 'Stage Time (min)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'numClusters',
        headerName: '# Clusters',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'stageLength',
        headerName: `Stage Length (${getMetricUnits('stageLength', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'tonsPerMin',
        headerName: `Prop. Pl. Eff. (${getMetricUnits('tonsPerMin', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fluidIntensity',
        headerName: `Fluid Intensity (${getMetricUnits('fluidIntensity', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'sandPerFluid',
        headerName: `Sand Per Fluid (${getMetricUnits('sandPerFluid', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fluidPerSand',
        headerName: `Fluid Per Sand (${getMetricUnits('fluidPerSand', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalFr',
        headerName: `Total DFR (${getMetricUnits('totalFr', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalFr2',
        headerName: `Total LFR (${getMetricUnits('totalFr2', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'tpm',
        headerName: `Proppant Intensity Planned (${getMetricUnits('tpm', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'tpmActual',
        headerName: `Proppant Intensity Actual (${getMetricUnits('tpm', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalProppant',
        headerName: `Total Proppant (${getMetricUnits('totalProppant', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'maxProppant',
        headerName: `Max Proppant (${getMetricUnits('maxProppant', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'frPerFluid',
        headerName: `DFR Per Fluid (${getMetricUnits('frPerFluid', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'frPerSand',
        headerName: `DFR Per Sand (${getMetricUnits('frPerSand', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'frPerFluid2',
        headerName: `LFR Per Fluid (${getMetricUnits('frPerFluid2', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'frPerSand2',
        headerName: `LFR Per Sand (${getMetricUnits('frPerSand2', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'breakdownPressure',
        headerName: `Breakdown Pressure (${getMetricUnits('breakdownPressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'preSandPressure',
        headerName: `Pre Sand Pr. (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'preSandRate',
        headerName: `Pre Sand Rate (${getMetricUnits('rate', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'preSandPNwb',
        headerName: `Pre Sand Pnwb (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'preSandPPerf',
        headerName: `Pre Sand Pperf (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'timeToPreSand',
        headerName: 'Time to Pre Sand (min)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'cleanVolumeToPreSand',
        headerName: `Clean Vol. to Pre Sand (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalSlurryVolume',
        headerName: `Total Slurry Vol. (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalCleanVolume',
        headerName: `Total Clean Vol. (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'totalProducedVolume',
        headerName: `Total Produced Vol. (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'overflushVolume',
        headerName: `Overflush Volume (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'averagePumpingPressure',
        headerName: `Average Pumping Pr. (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'averagePumpingRate',
        headerName: `Average Pumping Rate (${getMetricUnits('rate', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      // {
      //   field: 'wellSwapTime',
      //   headerName: 'Well Swap Time (min)',
      //   width: 150,
      //   editable: false,
      //   sortable: true,
      //   filterable: true
      // },
      {
        field: 'numShots',
        headerName: 'Pre Sand Open Perfs',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'pPerf',
        headerName: `Flush Pperf (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'n',
        headerName: 'Flush Open Perfs',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'perfEfficiency',
        headerName: 'Perf Efficiency (%)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'isip',
        headerName: `ISIP Surf. (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'isipBH',
        headerName: `ISIP BH (${getMetricUnits('pressure', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fracGradient',
        headerName: `"Frac Gradient" (${getMetricUnits('fracGradient', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'perfClosure',
        headerName: 'Rate Drop Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'plugSlip',
        headerName: 'Plug Slip Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'sandoff',
        headerName: 'Sandoff Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'isolationIssue',
        headerName: 'Isolation Issue Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'equipmentIssue',
        headerName: 'Equipment Issue Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'lostSuction',
        headerName: 'Lost Suction Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'other',
        headerName: 'Other Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fracfeed',
        headerName: 'Fracfeed Events',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'ballSeating',
        headerName: 'Ball Seating',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'aSqrtK',
        headerName: `A*sqrt(k) (${getMetricUnits('fracArea', units)}*mD^0.5)`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fracArea',
        headerName: `Frac Area (${getMetricUnits('fracArea', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'halfLength',
        headerName: `Half Length (${getMetricUnits('halfLength', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fdiXf',
        headerName: `FDI Xf (${getMetricUnits('xf', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fdiVFR',
        headerName: `FDI VFR (${getMetricUnits('volume', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'pumpdownISIP',
        headerName: `Pumpdown ISIP (${getMetricUnits('isip', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'postPerfISIP',
        headerName: `Post Perf ISIP (${getMetricUnits('isip', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'deltaISIP',
        headerName: `Delta ISIP (${getMetricUnits('isip', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'percentEnergyLost',
        headerName: 'Percent Energy Lost (%)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'topDepth',
        headerName: `Cluster Top (${getMetricUnits('depth', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'bottomDepth',
        headerName: `Cluster Bottom (${getMetricUnits('depth', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'averageDepth',
        headerName: `Depth of Calculation (${getMetricUnits('depth', units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      }
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: CustomToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            }
          }}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
          density="compact"
          style={{fontSize: '12px'}}
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
      </GridToolbarContainer>
    );
  };
};

